import { call, put, takeEvery, all, select } from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import axios from 'axios';
import {getEntityCall} from '../entities/sagas';
import DateTimeUtils from "../shared/utils/DateTimeUtils";
import StringUtils from "../shared/utils/StringUtils";
import _ from '../shared/utils/ObjectUtils';
import { getUserPageRefresh } from '../auth/sagas';

const controllerUrl = '/terminals/';

const searchTerminalsCall = searchFields => new Promise((resolve, reject) => {
    axios.get(controllerUrl + 'search', {
        params: {
            ...searchFields
        }
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const getTerminalListCall = (filter = {}) => new Promise((resolve, reject) => {

    if (!axios.defaults.headers.common['Authorization']) {
        axios.get(process.env.REACT_APP_BACKEND_URL + "/auth/read-session").then(response => {
            axios.defaults.headers.common["Authorization"] = "Bearer " + response.data;
            return axios.get(controllerUrl + 'list', {
                params: filter
            }).then(
                response => resolve(response.data),
                error => reject(error)
            );
        }).catch(e => console.log(e));
    }
    else{
    axios.get(controllerUrl + 'list', {
        params: filter
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
    }
});

const getTerminalCall = (id) => new Promise((resolve, reject) => {
    axios.get(controllerUrl + id).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const createTerminalCall = terminalData => new Promise((resolve, reject) => {
    axios.post(controllerUrl, terminalData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const editTerminalCall = (terminalId, terminalData) => new Promise((resolve, reject) => {
    axios.put(controllerUrl + terminalId, terminalData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const deleteTerminalCall = terminalId => new Promise((resolve, reject) => {
    axios.delete(controllerUrl + terminalId).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

function* getTerminalListRequest({ filter }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const terminalList = yield call(() => getTerminalListCall(filter));

        yield put({
            type: actionTypes.GET_TERMINAL_LIST_SUCCESS,
            payload: terminalList.map(terminal => ({ value: terminal.id, label: terminal.name }))
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_TERMINAL_LIST_FAILED
        });
    }
}

const getTerminalProfilesCall = () => new Promise((resolve, reject) => {
    axios.get(controllerUrl + "terminalProfiles").then(
        response => resolve(response.data),
        error => reject(error)
    )
})

function* searchTerminalsRequest({ searchFields }) {
    try {
        const searchResults = yield call(() => searchTerminalsCall(searchFields));
        const entityIds = searchResults.content.map(terminal => terminal.entityId);
        const uniqueEntityIds = [...new Set(entityIds)];

        const entities = yield all(uniqueEntityIds.map(id => call(getEntityCall, id)));

        const searchResultsWithEntities = {
            ...searchResults,
            content: searchResults.content.map(
                terminal => ({
                    ...terminal,
                    profile: StringUtils.stringNormalize(terminal.profile.value),
                    entity: entities.find(e => e.id === terminal.entityId),
                    lastCommunication: terminal.lastCommunicationTimestamp ? DateTimeUtils.getDateTimeFromUnix(terminal.lastCommunicationTimestamp) : null,
                    status: terminal.enabled ? "Enabled" : "Disabled",
                })
            )
        };

        yield put({
            type: actionTypes.TERMINAL_SEARCH_SUCCESS,
            payload: searchResultsWithEntities
        });
    } catch (e) {
        yield put({
            type: actionTypes.TERMINAL_SEARCH_FAILED,
            payload: e
        });
    }
}

function* getTerminalRequest({ terminalId }) {
    try {
        const terminal = yield call(getTerminalCall, terminalId);

        terminal.entity = terminal.entityId;
        terminal.timezone = terminal.timeZone;
        terminal.profile = terminal.profile.value;
        terminal.lastCommunication= terminal.lastCommunicationTimestamp ? DateTimeUtils.getDateTimeFromUnix(terminal.lastCommunicationTimestamp) : null;
        terminal.status = terminal.enabled ? "Enabled" : "Disabled"
        yield put({
            type: actionTypes.GET_TERMINAL_SUCCESS,
            payload: terminal
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_TERMINAL_FAILED,
        });
    }
}

function* createTerminalRequest({ terminalData }) {
    try {
        terminalData.entityId = terminalData.entity;
        terminalData.timeZone = terminalData.timezone;

        if (
            _.isEmpty(terminalData.location) ||
            (_.isUndefined(terminalData.location.latitude) && _.isUndefined(terminalData.location.longitude))) {
            terminalData.location = null;
        }

        yield call(createTerminalCall, terminalData);

        yield put({
            type: actionTypes.CREATE_TERMINAL_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.CREATE_TERMINAL_FAILED
        })
    }
}

function* editTerminalRequest({ terminalId, terminalData }) {
    try {
        terminalData.entityId = terminalData.entity;
        terminalData.timeZone = terminalData.timezone;
        delete terminalData.lastCommunication;
        delete terminalData.location;

        yield call(editTerminalCall, terminalId, terminalData);

        yield put({
            type: actionTypes.EDIT_TERMINAL_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.EDIT_TERMINAL_FAILED
        })
    }
}

function* deleteTerminalRequest({ terminalId }) {
    try {
        yield call(deleteTerminalCall, terminalId);

        yield put({
            type: actionTypes.DELETE_TERMINAL_SUCCESS
        })
    } catch (e) {
        yield put({
            type: actionTypes.DELETE_TERMINAL_FAILED
        })
    }
}

function* getTerminalProfiles() {
    try {
        let profiles = yield call(getTerminalProfilesCall);

        profiles = profiles.map(profile => {
            return {
                ...profile,
                label: profile.value
            }
        })

        yield put({
            type: actionTypes.GET_TERMINAL_PROFILE_LIST_SUCCESS,
            payload: profiles
        })
    } catch (e) {
        yield put({
            type: actionTypes.GET_TERMINAL_PROFILE_LIST_FAILED
        })
    }
}

const terminalSagas = [
    takeEvery(actionTypes.GET_TERMINAL_LIST_REQUEST, getTerminalListRequest),
    takeEvery(actionTypes.TERMINAL_SEARCH_REQUEST, searchTerminalsRequest),
    takeEvery(actionTypes.GET_TERMINAL_REQUEST, getTerminalRequest),
    takeEvery(actionTypes.CREATE_TERMINAL_REQUEST, createTerminalRequest),
    takeEvery(actionTypes.EDIT_TERMINAL_REQUEST, editTerminalRequest),
    takeEvery(actionTypes.DELETE_TERMINAL_REQUEST, deleteTerminalRequest),
    takeEvery(actionTypes.GET_TERMINAL_PROFILE_LIST_REQUEST, getTerminalProfiles)
];

export {
    terminalSagas,
    getTerminalCall,
    getTerminalListCall
};